// client/src/components/ui/states.tsx — loading / empty / error / forbidden · UI-003 (04 §5.11).
//
// „Every async surface implements all three" is the §5.11 rule, and the reason it is a rule is
// that the three failure modes it prevents all look like the same bug to a user: a table with
// headers and no rows that is actually still loading, an empty list that gives no way to add
// the first row, and a red box with no correlation id that support cannot act on.
//
// Making them components rather than a snippet to copy is what makes the rule checkable — a
// screen either imports these or it is visibly doing something else.
//
// SCR-SYS-403 lives here too (§8.20): a permission the client mirror does not hold renders
// `ForbiddenState` INSIDE the shell, never a blank page and never a redirect.

import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon, type IconName } from '@/components/icons';
import { toProblemDetails } from '@/api/problem-details';
import { staffRolesVisible } from '@/config/staff-roles';
import { useUiStore } from '@/stores/ui-store';

import { Glyph } from './Glyph';

/**
 * Skeletons, not a spinner: §5.11 prefers them for lists and cards because they reserve the
 * space the content will take, so the page does not jump when it arrives.
 *
 * `aria-busy` on the region is what tells a screen reader that this is a wait rather than the
 * content itself; `rows` should roughly match what is loading.
 *
 * Each row is the SHAPE of a row since the visual pass (D-1835) — a card with a leading square
 * and two lines of text — rather than one grey slab, because a slab reserves space and says
 * nothing about what will fill it. Every box is still a `.skeleton`, so §5.11's snippet and the
 * tests that count the boxes hold; the region, the label and the `rows` contract are untouched.
 */
export function LoadingState({ rows = 3, className = '' }: { rows?: number; className?: string }) {
  const { t } = useTranslation();
  return (
    <div className={`space-y-3 ${className}`} aria-busy="true" aria-label={t('common.loading')}>
      {Array.from({ length: rows }, (_, index) => (
        <div key={index} className="section-card flex items-center gap-4 py-4">
          <div className="skeleton h-10 w-10 shrink-0 rounded-xl" />
          <div className="min-w-0 flex-1 space-y-2">
            <div className="skeleton h-4 w-1/3 max-w-48" />
            <div className="skeleton h-3 w-2/3 max-w-96" />
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * The glyph at the top of an empty, forbidden or system state (D-1835): the icon in a tinted
 * square rather than a bare grey outline floating in white space. `tone` is the one thing the
 * three callers decide — an empty list is an invitation and wears the brand, a refusal and a
 * failure are neutral.
 */
function StateGlyph({ icon, tone }: { icon: IconName; tone: 'brand' | 'neutral' }) {
  // The shared `Glyph` (D-1842), centred; `lg` is the 56 px square the first round drew here.
  return <Glyph icon={icon} tone={tone} size="lg" className="mx-auto" />;
}

/**
 * An empty state EXPLAINS and OFFERS (§5.11): a list with nothing in it is the most common
 * first screen a new user sees, so it is the worst place to render nothing.
 *
 * `bare` is for an empty state that stands INSIDE another surface: a card that already has a
 * head, or a dialog. It drops the card and its large padding and keeps the glyph, the title,
 * the body and the action, because a card drawn inside a card is the layout the console ruling
 * names among its don'ts (§8, D-2360). The default is unchanged.
 */
export function EmptyState({
  icon,
  title,
  body,
  action,
  bare = false,
}: {
  icon: IconName;
  title: string;
  body: string;
  /** The primary action — „Adaugă prima proprietate". Absent only when there is genuinely none. */
  action?: ReactNode;
  /** Inside another card or a dialog: no card of its own, and the padding of a nested block. */
  bare?: boolean;
}) {
  return (
    <div className={bare ? 'py-6 text-center' : 'section-card py-12 text-center'}>
      <StateGlyph icon={icon} tone="brand" />
      <h3 className="mt-4 text-lg font-semibold text-balance text-gray-700">{title}</h3>
      <p className="mx-auto mt-1 max-w-prose text-sm text-pretty text-gray-500">{body}</p>
      {action ? <div className="mt-5">{action}</div> : null}
    </div>
  );
}

/**
 * The RFC 9457 renderer. Takes the raw thrown value rather than a parsed document so that a
 * caller cannot forget to parse it — `toProblemDetails` is total (see api/problem-details.ts).
 *
 * The correlation id is not decoration: §5.11 requires it on every error surface because it
 * is the only string that ties what the user saw to the request in Seq (02 §19).
 *
 * UI-004 adds the second half of that sentence: §6.4 prefills the support mailto with „the
 * current X-Correlation-Id if the modal was opened from an error state", and THIS is that
 * error state. Without the handoff the clause has no caller and the user retypes a GUID.
 */
export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const { t } = useTranslation();
  const problem = toProblemDetails(error);
  const openSupport = useUiStore((state) => state.openSupport);

  return (
    <div className="alert alert-error max-w-xl" role="alert">
      <Icon name="x-circle" className="h-6 w-6 shrink-0" />
      <div>
        <h3 className="font-bold">{t('errors.title')}</h3>
        <div className="text-sm">{problem.detail ?? t('errors.generic')}</div>
        {problem.correlationId ? (
          <div className="mt-1 text-xs">
            {t('errors.correlationId')}: <code>{problem.correlationId}</code>
          </div>
        ) : null}
      </div>
      <button
        type="button"
        className="btn btn-sm btn-ghost"
        onClick={() => {
          openSupport(problem.correlationId);
        }}
      >
        {t('common.support.button')}
      </button>
      {onRetry ? (
        <button type="button" className="btn btn-sm" onClick={onRetry}>
          {t('common.retry')}
        </button>
      ) : null}
    </div>
  );
}

/**
 * SCR-SYS-403 (§8.20). Reached two ways: a route whose permission the client mirror does not
 * hold (§9.2.2), and a 403 the API returned anyway — the mirror is UX, the API is enforcement,
 * so the two can legitimately disagree and both land here.
 */
export function ForbiddenState({ children }: { children?: ReactNode }) {
  const { t } = useTranslation();
  return (
    <div className="section-card py-12 text-center">
      <StateGlyph icon="lock-closed" tone="neutral" />
      <h1 className="mt-4 text-xl font-bold text-balance text-base-content">
        {t('errors.forbiddenTitle')}
      </h1>
      <p className="mx-auto mt-1 max-w-prose text-sm text-pretty text-gray-500">
        {/* „Administrator" is the hired manager's label, withdrawn from view (D-2820). */}
        {staffRolesVisible() ? t('errors.forbiddenBody') : t('errors.forbiddenBodyWithoutStaff')}
      </p>
      {children ? <div className="mt-4">{children}</div> : null}
    </div>
  );
}

/**
 * The shared body of SCR-SYS-404 and SCR-SYS-500 (§8.20).
 *
 * Presentational on purpose: §8.20 requires both screens to render INSIDE the active shell,
 * and „the active shell" is different above and below `/app` — the root router has none, the
 * app route has `AppShell`. Two callers, one appearance, and neither owns the frame.
 */
export function SystemState({
  icon,
  title,
  body,
  correlationId,
  actions,
}: {
  icon: IconName;
  title: string;
  body: string;
  /** Shown whenever the failure carried one — it is the support handle (§5.11, 02 §19). */
  correlationId?: string | undefined;
  actions?: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <div className="section-card mx-auto max-w-xl py-12 text-center">
      <StateGlyph icon={icon} tone="neutral" />
      <h1 className="mt-4 text-2xl font-bold tracking-tight text-balance text-base-content">
        {title}
      </h1>
      <p className="mt-2 text-pretty break-words text-base-content/70">{body}</p>
      {correlationId ? (
        <p className="mt-2 text-xs text-gray-500">
          {t('errors.correlationId')}: <code>{correlationId}</code>
        </p>
      ) : null}
      {actions ? <div className="mt-6 flex justify-center gap-2">{actions}</div> : null}
    </div>
  );
}
